import { applyMiddleware, createStore, compose } from 'redux';
import type { PreloadedState } from 'redux';
import createLoggerMiddleware from 'redux-logger';
import { persistStore } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import thunkMiddleware from 'redux-thunk';

import { LOGGING } from 'constants/all';

import history from 'utils/browserHistory';

import { createPersistedReducer } from './reducer';
import saga from './saga';

export let GlobalStore: AppStore | undefined = undefined;

//Prefer `import {stores}` over `import {GlobalStore}`:
//Why? stores is always defined. So if imported before configureStoreAndPersistor() is called, the global store will eventually be added to this object
//Whereas if `GlobalStore` is imported first, then it will be undefined and it doesn't later become defined in the imported context.
export const stores: { global?: AppStore } = {};

const composeEnhancers =
  window?.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ?? compose;

const loggerPredicate = () => {
  return LOGGING;
};

const sagaMiddleware = createSagaMiddleware({
  onError: (error) => {
    console.error(error);
  },
});

export const configureStore = (preloadedState?: PreloadedState<RootState>) => {
  const loggerMiddleware = createLoggerMiddleware({
    predicate: loggerPredicate,
    collapsed: true,
  });

  const middleware = [thunkMiddleware, loggerMiddleware, sagaMiddleware];

  const enhancer = composeEnhancers(applyMiddleware(...middleware));

  const store = createStore(
    createPersistedReducer(history),
    preloadedState ?? undefined,
    enhancer,
  );

  stores.global = GlobalStore = store;
  return store;
};

const configureStoreAndPersistor = () => {
  const store = configureStore(undefined);

  const persistor = persistStore(store, undefined, () => {
    sagaMiddleware.run(saga);
  });

  return { store, persistor };
};

export type RootState = ReturnType<ReturnType<typeof createPersistedReducer>>;
export type AppStore = ReturnType<typeof configureStore>;

export default configureStoreAndPersistor;
